import { Pipe, Inject, LOCALE_ID, Component, OnInit, ViewChild, HostListener, ElementRef, ViewChildren, QueryList, ChangeDetectorRef, Input, OnChanges, SimpleChanges, SimpleChange, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { FormGroup, FormBuilder, Validators, FormControl, FormArray, AbstractControl } from '@angular/forms';
import { Order, LogElement } from '@models/order';
import { Product, ProductElement, ProductQuery } from '@models/product';
import { Vendor } from '@models/vendor';
import { VendorOrder, VendorOrders } from '@models/vendor-order';
import { OrderService } from '@services/order.service';
import { ProductService } from '@services/product.service';
import { StoreService } from '@services/store.service';
import { MatTableDataSource, MatTable } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@environments/environment';
import { AuthService } from '@services/auth.service';
import { VendorOrderService } from '@services/vendor-order.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Observable, of, Subscription } from 'rxjs';
import { OrdersBaseComponent } from '@components/admin/managed-board/base/orders-base/orders-base.component';
import { NotificationService } from '@services/notification.service';
import { SettingsService } from '@services/settings.service';
import { OrderEditDialogComponent } from '@components/admin/patient/order-edit-dialog/order-edit-dialog.component';

@Component({
  selector: 'app-stack-orders',
  templateUrl: './stack-orders.component.html',
  styleUrls: ['../../managed-board.component.css', './stack-orders.component.scss'],
  animations: [
  trigger('detailExpand', [
    state('collapsed', style({ height: '0px', minHeight: '0', visibility: 'hidden' })),
    state('expanded', style({ height: '*', visibility: 'visible' })),
    transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ]
})
export class StackOrdersComponent extends OrdersBaseComponent implements OnInit {
	@Input() public vendorOrders: VendorOrders;
  @Input() public orderIndex: number;
  @Input() public tableView = 'orders';
  @Output() refreshOrders = new EventEmitter<void>();
	@ViewChild(MatSort, {static: false}) sort: MatSort;
  displayedColumns = ['alert', 'model', 'color', 'size', 'patientStock', 'patient', 'invoiceNumber', 'view', 'log'];
  displayedColumns2 = ['notes', 'cost', 'priceLink', 'retail', 'duty', 'action'];
  displayedColumns3 = ['notes', 'cost', 'priceLink', 'retail', 'duty', 'retailPlusDuty', 'action'];

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    public auth: AuthService,
    public orderService: OrderService,
    public vendorOrderService: VendorOrderService,
    public productService: ProductService,
    public storeService: StoreService,
    public cdRef: ChangeDetectorRef,
    public fb: FormBuilder,
    public route: ActivatedRoute,
    public notificationService: NotificationService,
    public settingsService: SettingsService,
    private dialog: MatDialog
  ) {
    super(locale, auth, orderService, vendorOrderService, productService, storeService, cdRef, fb, route, notificationService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.displayedColumns = this.columnsForView();
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  openOrderEdit($event: Event, order: FormGroup): void {
    $event.preventDefault();
    $event.stopPropagation();
    const patientId =
      order.value?.patient?._id || order.value?.patient || null;
    const orderId = order.value?._id;
    if (!patientId || !orderId) {
      return;
    }

    const dialogRef = this.dialog.open(OrderEditDialogComponent, {
      width: '96vw',
      maxWidth: '96vw',
      height: '92vh',
      maxHeight: '92vh',
      autoFocus: false,
      panelClass: 'order-edit-dialog-panel',
      data: { patientId, orderId },
    });

    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        this.refreshOrders.emit();
      }
    });
  }

  stackTodoLabel(order: FormGroup): string {
    const status = String(order?.value?.status || '').trim();
    if (
      status === 'TO ORDER' ||
      status === 'HOLD/RX' ||
      status === 'CALL FOR RX'
    ) {
      return status;
    }
    return order?.value?.todo || '';
  }

  stackEtaValue(order: FormGroup): string | Date {
    if (this.isHoldOrder(order) || this.isCallForRxOrder(order)) {
      return order?.value?.products?.[0]?.frame?.followUpDate || order?.value?.eta;
    }
    return order?.value?.eta;
  }

  isHoldOrder(order: FormGroup): boolean {
    const value = order?.value || {};
    return (
      value.todo === 'HOLD' ||
      value.status === 'HOLD/RX' ||
      value.status === 'HOLD / RX'
    );
  }

  isCallForRxOrder(order: FormGroup): boolean {
    const value = order?.value || {};
    return (
      value.todo === 'CALL' ||
      value.status === 'CALL FOR RX' ||
      value.rxStatus === 'CALL FOR RX' ||
      value.jobAction === 'CALL FOR RX' ||
      value.jobAction === 'CALL / RX' ||
      (Array.isArray(value.jobActions) &&
        (value.jobActions.includes('CALL FOR RX') ||
          value.jobActions.includes('CALL / RX')))
    );
  }

  needsFollowUpAlert(order: FormGroup): boolean {
    return this.isHoldOverdue(order) || this.isCallForRxOrder(order);
  }

  isHoldOverdue(order: FormGroup): boolean {
    if (!this.isHoldOrder(order)) {
      return false;
    }
    const followUp = order?.value?.products?.[0]?.frame?.followUpDate;
    if (!followUp) {
      return false;
    }
    const parsed = followUp instanceof Date ? followUp : new Date(followUp);
    if (isNaN(parsed.getTime())) {
      return false;
    }
    parsed.setHours(0, 0, 0, 0);
    return parsed.getTime() <= Date.now();
  }

  holdOverdueReason(order: FormGroup): string {
    if (this.isCallForRxOrder(order)) {
      return 'CALL FOR RX';
    }
    return this.isHoldPaid(order) ? 'FOLLOW UP FOR RX' : 'FOLLOW UP';
  }

  isHoldPaid(order: FormGroup): boolean {
    const value = order?.value || {};
    if (value.status === 'DISPENSED') {
      return true;
    }
    const log = Array.isArray(value.log) ? value.log : [];
    return log.some((entry) =>
      /PAID|PAYMENT|ADD TO CART|DEPOSIT|DISPENSED/i.test(String(entry?.item || ''))
    );
  }

  frameLabel(order: FormGroup): string {
    const product = order?.value?.products?.[0]?.product || {};
    return [product.model, product.color, product.size].filter(Boolean).join('  ');
  }

  vendorName(order: FormGroup): string {
    return String(order?.value?.products?.[0]?.product?.vendor?.name || '').trim() || '—';
  }

  orderDateValue(order: FormGroup): string | Date {
    return (
      order?.value?.products?.[0]?.frame?.orderDate ||
      order?.value?.date ||
      ''
    );
  }

  referenceNumber(order: FormGroup): string {
    return String(order?.value?.products?.[0]?.frame?.referenceNumber || '').trim();
  }

  paymentLabel(order: FormGroup): string {
    return this.isHoldPaid(order) ? 'DEPOSIT / PAID' : 'UNPAID';
  }

  private columnsForView(): string[] {
    switch (this.tableView) {
      case 'hold':
        return [
          'alert',
          'patient',
          'invoiceNumber',
          'frame',
          'eta',
          'payment',
          'view',
          'log',
        ];
      case 'awaiting':
        return [
          'patient',
          'invoiceNumber',
          'vendorName',
          'frame',
          'orderDate',
          'referenceNumber',
          'view',
          'log',
        ];
      default:
        return [
          'alert',
          'model',
          'color',
          'size',
          'patientStock',
          'patient',
          'invoiceNumber',
          'view',
          'log',
        ];
    }
  }
}
